import { Box, Button, Spinner, Text, VStack } from '@chakra-ui/react';
import CustomInputDateTimeRange from '@components/common/inputs/CustomInputDateTimeRange';
import { useReplayTimelineContext } from '@components/replay/ReplayTimelineContext';
import { DraggablePanel } from '@components/ui/draggable';
import { useDownloadSensorRaw } from '@hooks/download/useDownloadSensorRaw';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { closePopupByMapIdAndType } from '@redux/maps/maps.reducer';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetSensorsWithAvailableRawsForRangeQuery } from '@services/c2/c2.api';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { DateTimeRange } from '@/types/dateTime.types';
import { PopupTypeEnum } from '@/types/popup.types';

type Props = {
  position: { x: number; y: number };
};

export default function SensorRawExport({ position }: Readonly<Props>) {
  const { mapId, range } = useReplayTimelineContext();
  const { startDownload } = useDownloadSensorRaw();
  const activeSite = useAppSelector(selectActiveSite);

  const closePopupByMapAndType = useWithDispatch(closePopupByMapIdAndType);
  const closePopup = useCallback(
    () => closePopupByMapAndType({ mapId, type: PopupTypeEnum.SENSOR_RAW_EXPORT }),
    [closePopupByMapAndType, mapId],
  );

  const [dateFrom, setDateFrom] = useState<Date>(range.from);
  const [dateTo, setDateTo] = useState<Date>(range.to);

  const { data, isFetching } = useGetSensorsWithAvailableRawsForRangeQuery(
    activeSite ? { site: activeSite.code, from: dateFrom.toISOString(), to: dateTo.toISOString() } : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const availableSensorCodes = data?.sensorCodes ?? [];

  const doDownload = () =>
    startDownload({
      from: dateFrom.toISOString(),
      to: dateTo.toISOString(),
      sensorCodes: availableSensorCodes,
    });

  return (
    <DraggablePanel.Root top={position.y - 300} left={position.x - 630}>
      <DraggablePanel.Header onClose={closePopup}>
        <FormattedMessage id="sensorRawExport.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width="550px" padding={2} gap={2}>
        <Text>
          <FormattedMessage id="sensorRawExport.exportRange" />
        </Text>
        <VStack gap={2} alignItems={'center'} paddingX={2}>
          <Box padding={2} width="100%">
            <CustomInputDateTimeRange
              range={range}
              onChange={async (range: DateTimeRange) => {
                setDateFrom(range.from);
                setDateTo(range.to);
              }}
              singleLine={false}
            />
          </Box>
          {availableSensorCodes.length === 0 && (
            <Text>
              <FormattedMessage id="sensorRawExport.noData" />
            </Text>
          )}
          <Button
            width="248px"
            loading={isFetching}
            spinner={<Spinner color="neutral.200" />}
            onClick={() => doDownload()}
            disabled={availableSensorCodes.length === 0}
          >
            <FormattedMessage id="sensorRawExport.download" />
          </Button>
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
